import { useDialogs } from "@platform/hooks";
import { BenefitPlanTypeEnum } from "@platform/openapi-types";
import React from "react";
import {
  AddBenefitPlanGroupArgs,
  AddPlanDialogData,
  CommentDialogData,
  DeletePlanDialogData,
  DialogName,
  DuplicateBenefitPlanGroupArgs,
  DuplicatePlanDialogData,
  FIELD_COMMENT_MAX_LENGTH,
  NicknamePlanDialogData,
} from "@platform/plan-grid-core";

import {
  AddNewBenefitPlanDialog,
  CommentDialog,
  ConfirmDeletePlanDialog,
  NicknamePlanDialog,
} from "./ContextMenu";

interface PlanDialogRendererProps {
  onAddPlan: (args: AddBenefitPlanGroupArgs) => void;
  onNicknamePlan: (
    planGroupId: string | undefined,
    nickname: string,
    productType?: BenefitPlanTypeEnum,
  ) => void;
  onDeletePlan: (planGroupId: string) => void;
  onDeletePlanLevel: (data: DeletePlanDialogData) => void;
  onDuplicatePlan: (args: DuplicateBenefitPlanGroupArgs) => void;
}

export const PlanDialogRenderer: React.FC<PlanDialogRendererProps> = ({
  onAddPlan,
  onNicknamePlan,
  onDeletePlan,
  onDeletePlanLevel,
  onDuplicatePlan,
}) => {
  const { getDialog, closeDialog } = useDialogs<DialogName>();

  const commentDialog = getDialog<CommentDialogData>("comment");
  const addPlanDialog = getDialog<AddPlanDialogData>("addPlan");
  const nicknamePlanDialog = getDialog<NicknamePlanDialogData>("nickname");
  const deletePlanDialog = getDialog<DeletePlanDialogData>("deletePlan");
  const deletePlanLevelDialog =
    getDialog<DeletePlanDialogData>("deletePlanLevel");
  const duplicatePlanDialog =
    getDialog<DuplicatePlanDialogData>("duplicatePlan");

  return (
    <>
      {commentDialog?.isOpen && commentDialog.data && (
        <CommentDialog
          open
          title={commentDialog.data.title ?? "Needs Review"}
          currentComment={commentDialog.data.currentComment}
          maxLength={FIELD_COMMENT_MAX_LENGTH}
          onSubmit={(comment) => {
            commentDialog.data?.onSubmit(comment);
            closeDialog("comment");
          }}
          onCancel={() => closeDialog("comment")}
        />
      )}
      {addPlanDialog?.isOpen && addPlanDialog.data && (
        <AddNewBenefitPlanDialog
          currentPolicyPeriod={addPlanDialog.data.currentPolicyPeriod}
          coverageId={addPlanDialog.data.coverageId}
          planType={addPlanDialog.data.productType}
          onConfirm={(nickname, size, policyPeriod, currentPolicyUuid) => {
            if (!addPlanDialog.data || !policyPeriod) return;
            const productType = addPlanDialog.data.productType;
            const insertPosition = addPlanDialog.data.insertPosition;
            const position = addPlanDialog.data.position;
            onAddPlan({
              productType,
              size,
              nickname,
              insertPosition,
              position,
              policyPeriod,
              currentPolicyUuid,
            });
            closeDialog("addPlan");
          }}
          onCancel={() => closeDialog("addPlan")}
        />
      )}
      {nicknamePlanDialog?.isOpen && nicknamePlanDialog.data && (
        <NicknamePlanDialog
          currentNickname={nicknamePlanDialog.data.currentNickname}
          onConfirm={(nickname) => {
            onNicknamePlan(
              nicknamePlanDialog.data?.planGroupId,
              nickname,
              nicknamePlanDialog.data?.productType,
            );
            closeDialog("nickname");
          }}
          onCancel={() => closeDialog("nickname")}
        />
      )}
      {deletePlanDialog?.isOpen && deletePlanDialog.data && (
        <ConfirmDeletePlanDialog
          planGroupId={deletePlanDialog.data.planGroupId}
          planName={deletePlanDialog.data.planName}
          willPromote={deletePlanDialog.data.willPromote}
          onConfirm={(planGroupId) => {
            onDeletePlan(planGroupId);
            closeDialog("deletePlan");
          }}
          onCancel={() => closeDialog("deletePlan")}
        />
      )}
      {deletePlanLevelDialog?.isOpen && deletePlanLevelDialog.data && (
        <ConfirmDeletePlanDialog
          planGroupId={deletePlanLevelDialog.data.planGroupId}
          planName={deletePlanLevelDialog.data.planName}
          title="Delete Plan"
          onConfirm={() => {
            const dialogData = deletePlanLevelDialog.data;
            if (!dialogData) return;
            onDeletePlanLevel(dialogData);
            closeDialog("deletePlanLevel");
          }}
          onCancel={() => closeDialog("deletePlanLevel")}
        />
      )}
      {duplicatePlanDialog?.isOpen && duplicatePlanDialog.data && (
        <NicknamePlanDialog
          title="Duplicate Plan Group"
          currentNickname={duplicatePlanDialog.data.currentNickname}
          currentPolicyPeriod={duplicatePlanDialog.data.currentPolicyPeriod}
          coverageId={duplicatePlanDialog.data.coverageId}
          planType={duplicatePlanDialog.data.productType}
          onConfirm={(nickname, policyPeriod, currentPolicyUuid) => {
            if (!duplicatePlanDialog.data || !policyPeriod) return;
            const insertPosition = duplicatePlanDialog.data.insertPosition;
            const planGroupId = duplicatePlanDialog.data.planGroupId;
            onDuplicatePlan({
              nickname,
              insertPosition,
              planGroupId,
              policyPeriod,
              currentPolicyUuid,
            });
            closeDialog("duplicatePlan");
          }}
          onCancel={() => closeDialog("duplicatePlan")}
        />
      )}
    </>
  );
};

PlanDialogRenderer.displayName = "PlanDialogRenderer";
